import type { EstimateItemType } from '../types';
import type { CatalogKind } from '../data/categories/catalogLoader';

/**
 * utils/estimateLineTypes.ts — El tipo de una partida sale de su ORIGEN.
 *
 * POR QUÉ EXISTE:
 *   El tipo decide la REGLA de precio (Parte A.2 del plan): Material va por
 *   markup sobre el costo, Labor/Equipment/Subcontractor por margen sobre el
 *   precio, y Fee a costo pelado. Si el tipo se teclea a mano, la regla se
 *   elige a mano, y eso ya salió caro:
 *
 *   `components/estimate/EstimateToolPanel.tsx` (handleAddMaterial, :104-131)
 *   escribe `type: 'Material'` a pelo para TODO lo que se suelta desde la
 *   Biblioteca. Pero 58 filas de la lista de precios enlazadas al catálogo son
 *   `equipment_rental`: una excavadora de $800/día entraba como Material y
 *   cobraba el markup de material (20% → $960) en vez del margen de equipo
 *   (30% → $1.142,86). $182,86 por día regalados, en cada estimado, sin que
 *   nadie viera un número raro. Y los permisos entraban como Material también,
 *   con markup encima de un recibo de un tercero.
 *
 *   Esta función es el único sitio donde se traduce «lo que dice el catálogo»
 *   a «con qué regla se cotiza». Nadie más decide un tipo por su cuenta.
 *
 * EL MAPEO, COMPROBADO CONTRA PRODUCCIÓN (catalog_items, 2026-08-09):
 *   'material'          1.651 filas  → 'Material'
 *   'equipment_rental'    138 filas  → 'Equipment'
 *   'equipment_owned'       1 fila   → 'Equipment'
 *   'labor'                 0 filas  → 'Labor'   (el tipo existe en CatalogKind
 *                                      aunque hoy ninguna fila lo use; mapearlo
 *                                      cuesta una línea y evita que el día que
 *                                      aparezca se cotice como material)
 *   Categoría de permisos → 'Fee'
 *   Nada reconocible      → 'Material'  (el comportamiento de hoy, sin sorpresas)
 */

/**
 * Categorías del catálogo cuyas filas son cargos de un tercero que el
 * contratista REMITE: permisos, tasas de inspección, derechos de vía.
 *
 * Comprobado contra producción (2026-08-09): 'permits-and-fees' (39 filas) y
 * 'permits-fees' (6 filas, todas «Electrical permit — …»). Son la misma idea
 * escrita de dos maneras; el catálogo nunca se normalizó.
 *
 * NO entra 'fees' a secas (5 filas, todas de pintura) a propósito: ahí viven
 * un kit de prueba de plomo, bolsas de desecho RRP y un contenedor de basura.
 * Son consumibles y servicios que el contratista compra y revende con su
 * markup; llamarlos pase directo los vendería al costo y le quitaría margen
 * real. Un «fee» en el nombre de la categoría no es una prueba de que el
 * dinero sea de otro.
 */
const CATEGORIAS_DE_PASE_DIRECTO = new Set(['permits-and-fees', 'permits-fees']);

/** Los `default_kind` del catálogo que significan equipo, sea propio o alquilado. */
const KINDS_DE_EQUIPO = new Set<string>(['equipment_rental', 'equipment_owned']);

/**
 * El tipo de partida que le corresponde a una fila del catálogo.
 *
 * ORDEN DE DECISIÓN, y por qué es ese:
 *
 *   1. Un kind de EQUIPO gana siempre, incluso sobre la categoría de permisos.
 *      No es un detalle teórico: `concrete.concrete-pump-truck-service`
 *      («Concrete pump truck service, per day») está catalogado en
 *      'permits-and-fees' pero es `equipment_rental`, y lo mismo
 *      `painting.dumpster-haul-each`. Una bomba de concreto por día no es el
 *      recibo de nadie: es una renta con riesgo, y mandarla a pase directo la
 *      vendería al costo. La categoría describe dónde está guardada la fila;
 *      el kind describe qué es.
 *
 *   2. Después la categoría de permisos, y esto SÍ tiene que ganarle a
 *      `kind: 'material'`. Las 44 filas de permisos del catálogo declaran
 *      `default_kind = 'material'` —comprobado, no es un supuesto—, así que
 *      un orden estricto de «kind primero, categoría solo si no hay kind»
 *      jamás produciría un Fee y dejaría el pase directo sin implementar.
 *      Un permiso de $400 se remite en $400 (Parte A.2), venga como venga
 *      etiquetado en el catálogo.
 *
 *   3. Luego el kind normal.
 *
 *   4. Y si no se reconoce nada, 'Material': es lo que la app hace hoy, y esta
 *      ola es aditiva — sin información nueva no cambia ningún precio.
 *
 * @param kind     `catalog_items.default_kind` (o `pricebook`), si se conoce.
 * @param category `catalog_items.category`, para el respaldo de pase directo.
 */
export function lineTypeFromCatalogKind(
    kind?: CatalogKind | string | null,
    category?: string | null,
): EstimateItemType {
    const k = typeof kind === 'string' ? kind.trim().toLowerCase() : '';

    // 1. El equipo se reconoce por lo que es, no por dónde está archivado.
    if (KINDS_DE_EQUIPO.has(k)) return 'Equipment';

    // 2. Permisos y tasas: pase directo, aunque el catálogo los llame material.
    const c = typeof category === 'string' ? category.trim().toLowerCase() : '';
    if (CATEGORIAS_DE_PASE_DIRECTO.has(c)) return 'Fee';

    if (k === 'material') return 'Material';
    if (k === 'labor') return 'Labor';

    // 3. Sin kind ni categoría reconocible: como hoy.
    return 'Material';
}

/**
 * ¿La categoría de esta fila la manda a pase directo?
 *
 * Se exporta para que la interfaz pueda explicar POR QUÉ una fila entró como
 * Fee («viene de permisos y tasas») sin volver a escribir la lista de
 * categorías por su cuenta. La lista vive en un solo sitio, igual que la regla
 * de pase directo vive en `utils/passThroughPricing`.
 */
export function isPassThroughCategory(category?: string | null): boolean {
    const c = typeof category === 'string' ? category.trim().toLowerCase() : '';
    return CATEGORIAS_DE_PASE_DIRECTO.has(c);
}
